








































import { computed, defineComponent, PropType, ref, watch } from "@vue/composition-api";
import { ContentFile, ContentFileThumbnailType } from "@scrile/api-provider/dist/api/ContentFilesProvider";
import { MediaFilesController } from "@/lib/MediaFilesController";
import config from "@/config";
import { MAIN_CONTAINER_WIDTH, STORAGE } from "@/const";
import BaseVideo from "@/components/Base/BaseVideo";
import BaseDownloadButton from "@/components/BaseDownloadButton";
import PlayButton from "@/components/PlayButton";

export default defineComponent({
  name: "ContentFeedVideoGalleryItem",
  components: { BaseVideo, PlayButton, BaseDownloadButton },
  props: {
    showNotSafeForWork: { type: Boolean, required: true },
    file: { type: Object as () => ContentFile, required: true },
    thumbnails: { type: Array as PropType<ContentFile[]>, required: true },
    isFullScreen: { type: Boolean, default: false },
    accessed: { type: Boolean, default: false },
    active: { type: Boolean, default: false },
    forceThumbnailPreview: { type: String as () => ContentFileThumbnailType | null, default: null },
  },
  setup(props) {
    const dpr = Math.floor(window.devicePixelRatio);

    const manualPlay = ref(false);

    const sfwThumb = computed(() =>
      props.thumbnails.find(thumbnail => thumbnail.thumbnailType === ContentFileThumbnailType.SFW)
    );
    const nsfwThumb = computed(() =>
      props.thumbnails.find(thumbnail => thumbnail.thumbnailType === ContentFileThumbnailType.NSFW)
    );

    const thumbnailToShow = computed<ContentFile | null>(() => {
      // the preview toggle asks for one version explicitly, but a post may carry only the other one
      if (props.forceThumbnailPreview === ContentFileThumbnailType.SFW) {
        return sfwThumb.value ?? nsfwThumb.value ?? null;
      }
      if (props.forceThumbnailPreview === ContentFileThumbnailType.NSFW) {
        return nsfwThumb.value ?? null;
      }
      if (props.showNotSafeForWork && nsfwThumb.value) {
        return nsfwThumb.value;
      }
      return sfwThumb.value ?? null;
    });

    const thumbnailSrc = computed(() => {
      if (!thumbnailToShow.value) return "/img/content-preview-media.svg";
      if (thumbnailToShow.value.urlPart.startsWith("blob:")) return thumbnailToShow.value.urlPart;
      return MediaFilesController.getImageThumbnail(thumbnailToShow.value.urlPart, MAIN_CONTAINER_WIDTH * dpr, 0, {
        exactSize: true,
      });
    });

    const isPlayableVideo = computed(() => ["NEW", "CONVERTING"].indexOf(props.file.processingStatus) === -1);
    const showVideo = computed(
      () => props.showNotSafeForWork && (props.active || manualPlay.value) && isPlayableVideo.value
    );
    const isBlobUrl = computed(() => props.file.urlPart.startsWith("blob:"));
    const canDownloadFiles = computed(() => config.featureDownload && props.accessed && !isBlobUrl.value);
    const videoSrc = computed(() => {
      if (isBlobUrl.value) return props.file.urlPart;
      return MediaFilesController.getVideoUrl(props.file.urlPart);
    });
    const previewSrc = computed(() => {
      if (showVideo.value) {
        if (isBlobUrl.value) return "/img/content-no-preview.svg";
        return MediaFilesController.getVideoThumbnail(props.file.urlPart, MAIN_CONTAINER_WIDTH * dpr, 0, {
          exactSize: true,
        });
      }
      return thumbnailSrc.value;
    });
    const fullScreenImageSrc = computed(() => {
      if (isBlobUrl.value) return "/img/content-no-preview.svg";
      if (window.innerHeight <= MAIN_CONTAINER_WIDTH) return previewSrc.value;
      return MediaFilesController.getVideoThumbnail(props.file.urlPart, "1080p");
    });
    const imageSrc = computed(() => (props.isFullScreen ? fullScreenImageSrc.value : previewSrc.value));

    const onPlayClick = () => {
      manualPlay.value = true;
    };

    watch(
      () => props.active,
      v => {
        if (!v) manualPlay.value = false;
      }
    );

    return {
      isPlayableVideo,
      showVideo,
      videoSrc,
      imageSrc,
      previewSrc,
      canDownloadFiles,
      thumbnailSrc,
      onPlayClick,
      STORAGE,
    };
  },
});
